import type { TransactionApiItem } from '@/api/transaction-views/queries';
import { BottomSheet, Modal, Separator, Spinner, theme, Typography } from '@/design-system';
import { useIsMobile } from '@/global-hooks/use-is-mobile';

import { TransactionDetailDigest } from './transaction-detail-digest';
import { TransactionDetailFacts } from './transaction-detail-facts';
import { Body, Loading } from './transaction-detail-modal.styles';
import { TransactionDetailSummary } from './transaction-detail-summary';
import { TransactionLoadError } from './transaction-load-error';

interface TransactionDetailModalProps {
  open: boolean;
  detail: TransactionApiItem | undefined;
  isLoading: boolean;
  isError?: boolean;
  counterpartyDisplayNames?: ReadonlyMap<string, string>;
  onRetry?: () => Promise<unknown> | void;
  onClose: () => void;
}

export const TransactionDetailModal = ({
  open,
  detail,
  isLoading,
  isError = false,
  counterpartyDisplayNames,
  onRetry,
  onClose,
}: TransactionDetailModalProps) => {
  const isMobile = useIsMobile();
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) onClose();
  };
  const content = (
    <Body>
      {isLoading ? (
        <Loading role="status" aria-label="Loading transaction details">
          <Spinner color="primary" />
        </Loading>
      ) : detail ? (
        <TransactionDetailContent
          detail={detail}
          counterpartyDisplayNames={counterpartyDisplayNames}
        />
      ) : isError ? (
        <TransactionLoadError message="Unable to load transaction details." onRetry={onRetry} />
      ) : (
        <Typography variant="body2" color={theme.colors.text.secondary}>
          No transaction details found.
        </Typography>
      )}
    </Body>
  );

  if (isMobile) {
    return (
      <BottomSheet open={open} onOpenChange={handleOpenChange} title="Transaction details">
        {content}
      </BottomSheet>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      title="Transaction details"
      maxWidth={680}
      contentPadding={0}
    >
      {content}
    </Modal>
  );
};

const TransactionDetailContent = ({
  detail,
  counterpartyDisplayNames,
}: {
  detail: TransactionApiItem;
  counterpartyDisplayNames?: ReadonlyMap<string, string>;
}) => (
  <>
    <TransactionDetailSummary detail={detail} />
    {detail.onchain ? <Separator /> : null}
    <TransactionDetailFacts detail={detail} counterpartyDisplayNames={counterpartyDisplayNames} />
    {detail.onchain ? (
      <TransactionDetailDigest key={detail.onchain.hash} hash={detail.onchain.hash} />
    ) : null}
  </>
);
